import type { Mark } from "@/lib/types";
import { cn } from "@/lib/utils";

const ROWS = ["qwertyuiop", "asdfghjkl", "zxcvbnm"] as const;

const TONE: Record<Mark, string> = {
  hit: "key-hit",
  near: "key-near",
  miss: "key-miss",
};

type Props = {
  letters: Record<string, Mark>;
  disabled: boolean;
  onKey: (letter: string) => void;
  onEnter: () => void;
  onBack: () => void;
};

export function Keyboard({ letters, disabled, onKey, onEnter, onBack }: Props) {
  return (
    <div
      className={cn(
        // Tighter gaps below 400px: ten keys, nine gaps and 24px of page
        // padding is what decides whether a key is tappable on a small phone.
        "mx-auto flex w-full max-w-[500px] flex-col gap-1 select-none sm:gap-1.5",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      {ROWS.map((row, i) => (
        <div key={i} className="flex w-full gap-1 sm:gap-1.5">
          {/* Middle row is inset half a key, exactly like a real keyboard. */}
          {i === 1 && <span className="flex-[0.5]" />}
          {i === 2 && (
            <button type="button" className="key flex-[1.5]" onClick={onEnter}>
              Enter
            </button>
          )}

          {row.split("").map((letter) => (
            <button
              key={letter}
              type="button"
              className={cn("key", letters[letter] && TONE[letters[letter]!])}
              onClick={() => onKey(letter)}
            >
              {letter}
            </button>
          ))}

          {i === 1 && <span className="flex-[0.5]" />}
          {i === 2 && (
            <button type="button" className="key flex-[1.5]" onClick={onBack} aria-label="Backspace">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
                <path d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z" />
              </svg>
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
